import { Injectable } from '@angular/core';
import { NgbDateAdapter, NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

/**
 * ADM-678 (7ª iteración) — selector de fecha de los modales de pago.
 *
 * El API recibe `paymentDate` como string `'YYYY-MM-DD'` (certificado con Banesco)
 * y eso NO cambia. Lo que cambia es la experiencia: el cliente ve y escribe
 * `dd/mm/aaaa` en un `ngbDatepicker` con el calendario de Fina.
 *
 *  - `IsoStringDateAdapter`: el MODELO (`[(ngModel)]="paymentDate"`) sigue siendo
 *    el string ISO. Así las líneas `paymentDate: this.paymentDate` de los dos
 *    modales no se tocan.
 *  - `DdMmYyyyParserFormatter`: lo que se muestra y se teclea en el input.
 *
 * Los dos se proveen a nivel de componente (solo en los modales de crédito), no
 * globalmente: el resto de la app sigue con el formato por defecto de ng-bootstrap.
 */

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DMY_RE = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s*$/;

const pad = (n: number): string => String(n).padStart(2, '0');

/** `'YYYY-MM-DD'` → `{ year, month, day }`; `null` si no es una fecha ISO válida. */
export function isoToDateStruct(iso: string | null | undefined): NgbDateStruct | null {
    const m = typeof iso === 'string' ? ISO_RE.exec(iso) : null;
    if (!m) return null;
    const struct = { year: +m[1], month: +m[2], day: +m[3] };
    // Rechaza 2026-13-45 y similares: el día tiene que existir en ese mes.
    const check = new Date(struct.year, struct.month - 1, struct.day);
    const valid = check.getFullYear() === struct.year && check.getMonth() === struct.month - 1 && check.getDate() === struct.day;
    return valid ? struct : null;
}

/** `{ year, month, day }` → `'YYYY-MM-DD'`. */
export function dateStructToIso(date: NgbDateStruct | null): string | null {
    return date ? `${date.year}-${pad(date.month)}-${pad(date.day)}` : null;
}

/** `true` solo si el valor es un string `'YYYY-MM-DD'` de una fecha real. */
export function isIsoDate(value: unknown): boolean {
    return typeof value === 'string' && isoToDateStruct(value) !== null;
}

/**
 * Hoy en hora LOCAL del navegador, `'YYYY-MM-DD'`.
 *
 * Antes se usaba `new Date().toISOString().slice(0, 10)`, que es UTC: en
 * Venezuela (UTC-4) a partir de las 20:00 devolvía la fecha de mañana, y el
 * banco no encuentra un pago "de mañana".
 */
export function localTodayIso(): string {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Día calendario de Caracas (UTC-4 fijo) de un instante, `'YYYY-MM-DD'`; `null` sin fecha.
 * Mismo criterio que el API para la fecha mínima del pago: no puede ser anterior al
 * desembolso del crédito.
 */
export function caracasDayIso(value: string | Date | null | undefined): string | null {
    if (value == null || value === '') return null;
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getTime() - 4 * 3600 * 1000).toISOString().slice(0, 10);
}

/**
 * Fecha con la que se juzga el fee de reactivación de un pago: el día del pago que eligió el
 * comercio (mediodía UTC, para que el día no se corra), nunca después de hoy. Espejo de
 * `feeAsOf` del API: un pago hecho dentro de la gracia no carga el fee aunque se reporte
 * después. Sin fecha válida, `now`.
 */
export function paymentAsOf(paymentDate: string | null | undefined, now: Date): Date {
    if (!isIsoDate(paymentDate)) return now;
    const day = new Date(`${paymentDate}T12:00:00.000Z`);
    return day.getTime() < now.getTime() ? day : now;
}

/** `'YYYY-MM-DD'` → `'dd/mm/yyyy'` para mostrar; devuelve el valor tal cual si no es ISO. */
export function formatDdMmYyyy(iso: string | null | undefined): string {
    const s = isoToDateStruct(iso);
    return s ? `${pad(s.day)}/${pad(s.month)}/${s.year}` : iso ?? '';
}

/** Modelo del ngbDatepicker = string `'YYYY-MM-DD'`, lo que el API espera. */
@Injectable()
export class IsoStringDateAdapter extends NgbDateAdapter<string> {
    fromModel(value: string | null): NgbDateStruct | null {
        return isoToDateStruct(value);
    }

    toModel(date: NgbDateStruct | null): string | null {
        return dateStructToIso(date);
    }
}

/** Texto del input = `dd/mm/aaaa`; acepta `d/m/aaaa` al teclear. */
@Injectable()
export class DdMmYyyyParserFormatter extends NgbDateParserFormatter {
    parse(value: string): NgbDateStruct | null {
        const m = DMY_RE.exec(value || '');
        if (!m) return null;
        // Reusa la validación de día real pasando por ISO.
        return isoToDateStruct(`${m[3]}-${pad(+m[2])}-${pad(+m[1])}`);
    }

    format(date: NgbDateStruct | null): string {
        return date ? `${pad(date.day)}/${pad(date.month)}/${date.year}` : '';
    }
}
